import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const links = [
  { to: "/about", sectionId: "about", label: "About Us", end: false },
  { to: "/services", sectionId: "services", label: "Our Services", end: false },
  { to: "/products", sectionId: "products", label: "Our Products", end: false },
];

interface NavbarProps {
  variant?: "overlay" | "solid";
}

export default function Navbar({ variant = "overlay" }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === "/";

  const handleNavClick = (e: React.MouseEvent, sectionId: string) => {
    if(!isHome) return;
    e.preventDefault();

    document.querySelector(`#${sectionId}`)?.scrollIntoView({behavior:"smooth"})
    setOpen(false);
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = variant === "solid" || scrolled;

  const bgStyle = solid
    ? undefined
    : isHome
      ? undefined
      : { backgroundColor: "#ffffffb0" };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-cream/85 backdrop-blur-md border-b border-ink/5"
          : isHome
            ? "bg-transparent"
            : "backdrop-blur-sm border-b border-white/10"
      }`}
      style={bgStyle}
    >
      <div className="mx-auto flex h-[72px] w-full items-center justify-between px-6 md:px-10">
        <Link
          to="/"
          className={`flex items-center gap-2 cursor-pointer ${
            solid ? "text-ink" : "text-ink"
          }`}
        >
          <span className="text-display text-2xl font-semibold tracking-tight">
            Vantory<span className="italic font-light">/Labs</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={(e) => handleNavClick(e, l.sectionId)}
              className={({ isActive }) =>
                `text-xs uppercase tracking-widest2 font-medium cursor-pointer transition-colors hover:text-ink ${
                  isActive && !isHome ? "text-ink" : "text-ink/70"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/contact"
            className="group relative inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-xs font-medium uppercase tracking-widest2 text-cream whitespace-nowrap cursor-pointer hover:bg-terracotta transition-colors duration-300"
          >
            Contact Us
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-base group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </span>
          </Link>
        </div>

        <button
          aria-label="Open menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-ink/15 text-ink cursor-pointer"
        >
          <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-xl`} />
        </button>
      </div>

      {open ? (
        <div className="md:hidden border-t border-ink/10 bg-cream/95 backdrop-blur">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={(e) => handleNavClick(e, l.sectionId)}
                className={({ isActive }) =>
                  `text-base cursor-pointer ${isActive && !isHome ? "text-ink font-medium" : "text-ink/80"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-2 inline-flex items-center justify-center rounded-full bg-ink px-5 py-3 text-xs uppercase tracking-widest2 text-cream cursor-pointer whitespace-nowrap"
            >
              Contact Us
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  );
}